import {useContext} from 'react';

import {GlobalBannerContext} from '../components/GlobalBanners/GlobalBannerContextProvider';
import {AccountSelectorContext} from '../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../components/Search/SearchNav/types';
import {envIsIntegration, envIsPublicCloud} from '../config';
import {
  useOrganizationMemberWeaveRoleQuery,
  useTeamOrganizationMembersQuery,
} from '../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../state/viewer/hooks';
import {
  getPrimarySub,
  isProPrimaryPlan,
  isTeamsPrimaryPlan,
  isTrialPlanPrimaryPlan,
  useAccountPrivileges,
  usePrimarySubscriptionPlan,
} from '../util/accounts/pricing';

export const useWeaveAccess = (
  isPublicOrOpenProject: boolean,
  isAdminMode: boolean,
  entityName: string
) => {
  const viewer = useViewer();
  const {data, loading: isTeamOrganizationMembersLoading} =
    useTeamOrganizationMembersQuery({
      variables: {entityName: entityName},
    });
  const isUserProjectOrgMember = viewer?.organizations.some(
    org => org.id === data?.entity?.organizationId
  );
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const username = selectedAccount?.username ?? viewer?.username;
  // We should skip this check for SaaS personal entities, admin mode, and public/open projects
  const shouldSkipWeaveRoleCheck =
    (envIsPublicCloud &&
      selectedAccount?.accountType === AccountType.Personal) ||
    envIsIntegration ||
    isAdminMode ||
    isPublicOrOpenProject ||
    username == null;
  const {data: weaveRoleData, loading: isOrgMemberLoading} =
    useOrganizationMemberWeaveRoleQuery({
      variables: {orgName: orgName, username: username},
      skip: shouldSkipWeaveRoleCheck,
    });
  const {states} = useContext(GlobalBannerContext);
  const {privileges} = useAccountPrivileges(selectedAccount);
  const {plan, loading: primaryPlanLoading} =
    usePrimarySubscriptionPlan(selectedAccount);
  if (
    isTeamOrganizationMembersLoading ||
    primaryPlanLoading ||
    isOrgMemberLoading
  ) {
    return {
      isLoading: true,
      isWeaveLimitReached: false,
      isWeaveEnterpriseTrialExpired: false,
    };
  }
  const primarySub =
    plan != null ? getPrimarySub({subscriptions: [{plan}]}) : null;
  const isWeaveUnrestrictedPlan =
    primarySub != null &&
    (isProPrimaryPlan(primarySub.plan) ||
      selectedAccount?.accountType === AccountType.Academic ||
      isTeamsPrimaryPlan(primarySub.plan) ||
      isTrialPlanPrimaryPlan(primarySub.plan));
  const currentBannerState = states?.find(
    state => state.account.id === selectedAccount?.id
  );
  const weaveEnforcementFlags = currentBannerState?.weaveEnforcementBannerFlags;
  const weaveUsageBytes = weaveEnforcementFlags?.weaveUsageBytes;
  const weaveLimitBytes = weaveEnforcementFlags?.weaveLimitBytes;
  const usageExceeded =
    weaveUsageBytes != null &&
    weaveLimitBytes != null &&
    weaveUsageBytes >= weaveLimitBytes;
  const isWeaveLimitReached = usageExceeded && !isWeaveUnrestrictedPlan;
  const isWeaveEnterpriseTrialExpired =
    weaveEnforcementFlags?.weaveTrialBannerProps != null &&
    currentBannerState?.account.isEnterprise &&
    new Date(privileges.weaveLimitDateInterval.end) < new Date();
  if ((isPublicOrOpenProject && !isUserProjectOrgMember) || isAdminMode) {
    return {
      isWeaveLimitReached: false,
      isWeaveEnterpriseTrialExpired: false,
      hasWeaveAccess: true,
    };
  }
  const weaveRole = weaveRoleData?.organization?.member?.weaveRole;
  const hasWeaveAccess = shouldSkipWeaveRoleCheck
    ? true
    : weaveRole != null && weaveRole !== 'none';
  return {
    isWeaveLimitReached,
    isWeaveEnterpriseTrialExpired,
    hasWeaveAccess,
  };
};
